// src/features/insights/services/semanticSearchService.js
// Semantic Search Service - finds similar past entries using embeddings
// Part of Personal AI Memory System - Phase 4.2

import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import logger from '@/shared/utils/core/logger';

/**
 * Find similar past entries using semantic search
 * @param {string} userId - User ID
 * @param {string} currentContent - Current entry content
 * @param {string} token - Auth token
 * @param {number} limitResults - Number of similar entries to return
 * @returns {Promise<Array>} - Array of similar entries
 */
export async function findSimilarEntries(userId, currentContent, token, limitResults = 3) {
  if (!userId || !currentContent) {
    logger.warn('[SemanticSearch] Missing userId or content');
    return [];
  }

  try {
    logger.debug('[SemanticSearch] Finding similar entries for user:', userId);

    // For now, use a simple keyword-based approach
    // In production, this would call a Cloud Function with embeddings
    const keywords = extractKeywords(currentContent);

    // Get recent entries (last 90 days)
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const entriesQuery = query(
      collection(db, COLLECTIONS.JOURNAL_ENTRIES),
      where('uid', '==', userId),
      where('createdAt', '>=', Timestamp.fromDate(ninetyDaysAgo)),
      orderBy('createdAt', 'desc'),
      limit(50) // Get last 50 entries
    );

    const entriesSnapshot = await getDocs(entriesQuery);
    const entries = entriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate similarity scores (current entry hasn't been saved yet so it's
    // not in Firestore — no need to exclude by ID)
    const scoredEntries = entries
      .map(entry => {
        const entryText = entry.content || entry.extractedText || '';
        const similarity = calculateSimpleSimilarity(currentContent, entryText, keywords);

        return {
          id: entry.id,
          content: entryText.substring(0, 200), // First 200 chars
          mood: entry.mood,
          createdAt: entry.createdAt,
          similarity,
          date: entry.createdAt?.toDate?.() || new Date(entry.createdAt)
        };
      })
      .filter(entry => entry.similarity > 0.2) // Minimum similarity threshold
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, limitResults);

    logger.debug('[SemanticSearch] Found similar entries:', scoredEntries.length);

    return scoredEntries;

  } catch (error) {
    logger.error('[SemanticSearch] Failed to find similar entries:', error);
    return [];
  }
}

/**
 * Extract keywords from content (simple implementation)
 */
function extractKeywords(content) {
  if (!content) return [];

  const text = typeof content === 'string' ? content : content.text || '';

  // Common stop words to filter out
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during',
    'i', 'me', 'my', 'myself', 'we', 'our', 'you', 'your', 'he', 'him',
    'his', 'she', 'her', 'it', 'its', 'they', 'them', 'their', 'what',
    'which', 'who', 'when', 'where', 'why', 'how', 'all', 'each', 'every',
    'both', 'few', 'more', 'most', 'other', 'some', 'such', 'than', 'too',
    'very', 'can', 'will', 'just', 'should', 'now', 'am', 'is', 'are', 'was',
    'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did'
  ]);

  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Remove punctuation
    .split(/\s+/)
    .filter(word => word.length > 3) // Min 4 chars
    .filter(word => !stopWords.has(word));

  // Count word frequency
  const wordFreq = {};
  words.forEach(word => {
    wordFreq[word] = (wordFreq[word] || 0) + 1;
  });

  // Get top 10 keywords by frequency
  return Object.entries(wordFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([word]) => word);
}

/**
 * Calculate similarity between current content and past entry
 * Simple implementation using keyword overlap and fuzzy matching
 */
function calculateSimpleSimilarity(currentContent, pastContent, keywords) {
  const current = typeof currentContent === 'string'
    ? currentContent.toLowerCase()
    : (currentContent.text || '').toLowerCase();
  const past = pastContent.toLowerCase();

  // Keyword overlap score
  const keywordMatches = keywords.filter(keyword =>
    past.includes(keyword)
  ).length;
  const keywordScore = keywords.length > 0 ? keywordMatches / keywords.length : 0;

  // Word set overlap (Jaccard similarity)
  const currentWords = new Set(current.split(/\s+/));
  const pastWords = new Set(past.split(/\s+/));
  const intersection = new Set([...currentWords].filter(x => pastWords.has(x)));
  const union = new Set([...currentWords, ...pastWords]);
  const jaccardScore = union.size > 0 ? intersection.size / union.size : 0;

  // Combined score (weighted average)
  const similarity = (keywordScore * 0.7) + (jaccardScore * 0.3);

  return Number(similarity.toFixed(2));
}

/**
 * Format similar entries for AI context
 */
export function formatSimilarEntriesForContext(similarEntries) {
  if (!similarEntries || similarEntries.length === 0) {
    return '';
  }

  return similarEntries.map((entry, index) => {
    const date = entry.date instanceof Date
      ? entry.date.toLocaleDateString()
      : new Date(entry.date).toLocaleDateString();

    return `${index + 1}. ${date} (${Math.round(entry.similarity * 100)}% similar${entry.mood ? `, mood: ${entry.mood}` : ''})\n   "${entry.content}${entry.content.length >= 200 ? '...' : ''}"`;
  }).join('\n\n');
}

/**
 * Get emotionally similar entries (same mood)
 */
export async function findSimilarMoodEntries(userId, currentMood, limitResults = 3) {
  if (!userId || !currentMood) {
    return [];
  }

  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const moodQuery = query(
      collection(db, COLLECTIONS.JOURNAL_ENTRIES),
      where('uid', '==', userId),
      where('mood', '==', currentMood),
      where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
      orderBy('createdAt', 'desc'),
      limit(limitResults)
    );

    const snapshot = await getDocs(moodQuery);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      content: (doc.data().content || doc.data().extractedText || '').substring(0, 200),
      mood: doc.data().mood,
      createdAt: doc.data().createdAt,
      date: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));

  } catch (error) {
    logger.error('[SemanticSearch] Failed to find similar mood entries:', error);
    return [];
  }
}

export default {
  findSimilarEntries,
  formatSimilarEntriesForContext,
  findSimilarMoodEntries
};
